import { makeAutoObservable } from 'mobx'

import i18n from 'app/i18/i18'

import { GuestsLimits } from 'entities/kyrgyzstan/config/guests'

import type { KyrgyzstanCollectionPreset } from 'shared/api/kyrgyzstan/collections'
import {
  KyrgyzstanSearchApi,
  RealtyCity,
  type RealtyObject,
  type RealtySearchRequest,
} from 'shared/api/kyrgyzstan/search'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { dateFormat } from 'shared/lib/dateFormat'
import { getApiErrorMessage } from 'shared/lib/errorText'
import { simpleIsEqual } from 'shared/lib/simpleIsEqual'

import {
  type IRange,
  calculateNights,
  normalizeChildrenAges,
  prepareRequestParams,
} from '../../lib'
import type { GuestsState } from '../guests'

import { defaultSortOptionValue } from './sort'

export const DefaultGuests: GuestsState = {
  adults: GuestsLimits.adults.default,
  children: GuestsLimits.children.default,
  childrenAges: [],
}

class KyrgyzstanSearchStore {
  realtyList: RealtyObject[] = []

  isLoading = false

  error: string | null = null

  page = 1

  pageSize = 25

  hasMore = false

  totalCount = 0

  sort: string = defaultSortOptionValue

  abortController: AbortController | null = null

  previousRequestParams: RealtySearchRequest | null = null

  start_date?: string

  end_date?: string

  cities: RealtyCity[] = []

  guests?: GuestsState = DefaultGuests

  realty_type_ids?: number[]

  availableMinPrice: number = 0

  availableMaxPrice: number = 100000

  price_min?: number

  price_max?: number

  constructor() {
    makeAutoObservable(this)
  }

  get destinationDisplay(): string | undefined {
    if (this.cities.length === 0) return undefined

    if (this.cities.length === 1) {
      const [city] = this.cities
      return `${city.city_name}${city.region ? `, ${city.region}` : ''}`
    }

    return this.cities.map((city) => city.city_name).join(', ')
  }

  get datesDisplay(): string | undefined {
    if (!this.start_date || !this.end_date) return undefined

    const startFormatted = dateFormat(this.start_date, DATE_FORMAT_LIST.DAY)
    const endFormatted = dateFormat(this.end_date, DATE_FORMAT_LIST.DATE_MONTH)

    const nights = calculateNights(this.start_date, this.end_date)

    const nightsText = i18n.t('kyrgyzstan_block.search_result.nights', {
      count: nights,
    })

    return `${startFormatted} – ${endFormatted}, ${nightsText}`
  }

  get guestsCount(): number | undefined {
    if (!this.guests) return undefined
    return this.guests.adults + this.guests.children
  }

  get visitorsCount(): number | undefined {
    return this.guests?.adults
  }

  get childrenAges(): number[] {
    return normalizeChildrenAges(this.guests)
  }

  get guestsDisplay(): string | undefined {
    const adultsCount = this.guests?.adults
    const childrenCount = this.guests?.children

    if (!adultsCount && !childrenCount) return undefined

    const text = []
    if (adultsCount) {
      text.push(
        i18n.t('kyrgyzstan_block.search_form.adults', {
          count: adultsCount,
        }),
      )
    }
    if (childrenCount) {
      text.push(
        i18n.t('kyrgyzstan_block.search_form.child', {
          count: childrenCount,
        }),
      )
    }

    return text.join(', ')
  }

  setLoading = (loading: boolean) => {
    this.isLoading = loading
  }

  setError = (error: string | null) => {
    this.error = error
  }

  setRealtyList = (list: RealtyObject[]) => {
    this.realtyList = list
  }

  appendRealtyList = (list: RealtyObject[]) => {
    this.realtyList = [...this.realtyList, ...list]
  }

  setPage = (page: number) => {
    this.page = page
  }

  setSort = (sort: string) => {
    this.sort = sort
    this.page = 1
    this.realtyList = []
  }

  setHasMore = (hasMore: boolean) => {
    this.hasMore = hasMore
  }

  setTotalCount = (count: number) => {
    this.totalCount = count
  }

  setStartDate = (date?: string) => {
    this.start_date = date
  }

  setEndDate = (date?: string) => {
    this.end_date = date
  }

  setDates = (dates: IRange) => {
    this.start_date = dates.startDate
    this.end_date = dates.endDate
  }

  setCities = (cities: RealtyCity[]) => {
    this.cities = cities
  }

  setGuests = (guests: GuestsState) => {
    this.guests = guests
  }

  applyCollectionPreset = (collection: KyrgyzstanCollectionPreset) => {
    if (collection.start_date) {
      this.start_date = collection.start_date
    }

    if (collection.end_date) {
      this.end_date = collection.end_date
    }

    this.guests = {
      adults: collection.visitors_count || DefaultGuests.adults,
      children: DefaultGuests.children,
      childrenAges: [],
    }
  }

  setRealtyTypeIds = (realty_type_ids?: number[]) => {
    this.realty_type_ids = realty_type_ids
  }

  setAvailableMinPrice = (price: number) => {
    this.availableMinPrice = price
  }

  setAvailableMaxPrice = (price: number) => {
    this.availableMaxPrice = price
  }

  setPriceRange = (min?: number, max?: number) => {
    this.price_min = min
    this.price_max = max
  }

  reset = () => {
    this.cancelActiveRequest()

    this.realtyList = []
    this.page = 1
    this.isLoading = false
    this.error = null
    this.hasMore = false
    this.totalCount = 0
    this.previousRequestParams = null
  }

  cancelActiveRequest = () => {
    if (!this.abortController) {
      return
    }

    this.abortController.abort()
    this.abortController = null
    this.setLoading(false)
  }

  resetSearchParams = () => {
    this.start_date = undefined
    this.end_date = undefined
    this.cities = []
    this.guests = undefined
    this.realty_type_ids = undefined
  }

  resetFilters = () => {
    this.realty_type_ids = undefined
    this.price_min = undefined
    this.price_max = undefined
  }

  fetchRealtyList = async (params?: Partial<RealtySearchRequest>) => {
    this.setLoading(true)
    this.setError(null)

    let abortController: AbortController | null = null

    try {
      const requestPage = params?.page || this.page
      const isNewSearch = requestPage === 1

      const requestParams = prepareRequestParams({
        params,
        page: this.page,
        pageSize: this.pageSize,
        sort: this.sort,
        start_date: this.start_date,
        end_date: this.end_date,
        cities: this.cities,
        visitorsCount: this.visitorsCount,
        childrenAges: this.childrenAges,
        realty_type_ids: this.realty_type_ids,
        price_min: this.price_min,
        price_max: this.price_max,
      })

      if (!requestParams) {
        this.setError(i18n.t('kyrgyzstan_block.error.title'))
        return
      }

      if (this.previousRequestParams) {
        const paramsForComparison = isNewSearch
          ? {
              ...requestParams,
              page: undefined,
              page_size: undefined,
            }
          : requestParams

        const previousParamsForComparison = isNewSearch
          ? {
              ...this.previousRequestParams,
              page: undefined,
              page_size: undefined,
            }
          : this.previousRequestParams

        if (simpleIsEqual(paramsForComparison, previousParamsForComparison)) {
          this.setLoading(false)
          return
        }
      }

      if (isNewSearch && this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }

      abortController = new AbortController()

      this.abortController = abortController

      const { data } = await KyrgyzstanSearchApi.getRealtyList(
        requestParams,
        abortController.signal,
      )

      if (
        abortController.signal.aborted ||
        this.abortController !== abortController
      ) {
        return
      }

      if (isNewSearch) {
        this.setRealtyList(data.data)
      } else {
        this.appendRealtyList(data.data)
      }

      this.setPage(data.page)
      this.setHasMore(data.page < data.total_pages)
      this.setTotalCount(data.total_items)

      if (data.min_price !== undefined) {
        this.setAvailableMinPrice(data.min_price)
      }
      if (data.max_price !== undefined) {
        this.setAvailableMaxPrice(data.max_price)
      }

      this.previousRequestParams = requestParams
    } catch (error) {
      if (
        abortController &&
        (abortController.signal.aborted ||
          this.abortController !== abortController)
      ) {
        return
      }

      const errorMessage = getApiErrorMessage(error)

      if (errorMessage !== null) {
        this.setError(errorMessage)
      }
    } finally {
      if (!abortController || this.abortController === abortController) {
        if (this.abortController === abortController) {
          this.abortController = null
        }

        this.setLoading(false)
      }
    }
  }

  loadMore = async () => {
    if (this.isLoading || !this.hasMore) {
      return
    }

    await this.fetchRealtyList({ page: this.page + 1 })
  }
}

export const kyrgyzstanSearchStore = new KyrgyzstanSearchStore()
